let active = null;
let saved = null;
const BLOCKS = "p,div,h1,h2,h3,h4,h5,h6,li,blockquote,pre,td,th";

const regionOf = (node) => {
  const el = node?.nodeType === 3 ? node.parentElement : node;
  return el?.closest?.(".lw-editable") || null;
};

export function trackSelection() {
  const h = () => {
    const s = window.getSelection();
    if (!s.rangeCount) return;
    const r = s.getRangeAt(0);
    const reg = regionOf(r.commonAncestorContainer);
    if (reg) { active = reg; saved = r.cloneRange(); }
  };
  document.addEventListener("selectionchange", h);
  return () => document.removeEventListener("selectionchange", h);
}

export const setActiveRegion = (el) => { active = el; saved = null; };
export const getSavedRange = () => saved;
export const setSavedRange = (r) => { saved = r; };

export function insertBlock(html) {
  restore();
  const s = window.getSelection();
  const n = s.anchorNode;
  const el = n?.nodeType === 3 ? n.parentElement : n;
  const b = el?.closest?.("p,h1,h2,h3,h4,h5,h6,li,blockquote,pre");
  if (!b || !active?.contains(b) || !b.textContent.trim()) return exec("insertHTML", html);
  const t = document.createElement("div");
  t.innerHTML = html;
  const nodes = [...t.childNodes];
  b.after(...nodes);
  const r = document.createRange();
  r.selectNodeContents(nodes[nodes.length - 1]);
  r.collapse(false);
  s.removeAllRanges();
  s.addRange(r);
  emit();
}
export const getActive = () => active;

export function restore() {
  if (!active) return;
  active.focus({ preventScroll: true });
  const s = window.getSelection();
  s.removeAllRanges();
  if (saved && active.contains(saved.commonAncestorContainer)) s.addRange(saved);
  else {
    const r = document.createRange();
    r.selectNodeContents(active);
    r.collapse(false);
    s.addRange(r);
  }
}

export const emit = () => active?.dispatchEvent(new Event("input", { bubbles: true }));

let historyHook = null;
export const setHistoryHook = (fn) => { historyHook = fn; };

export function exec(cmd, val = null) {
  if ((cmd === "undo" || cmd === "redo") && historyHook?.(cmd)) return;
  restore();
  document.execCommand("styleWithCSS", false, true);
  document.execCommand(cmd, false, val);
  emit();
}

export const insertHTML = (html) => exec("insertHTML", html);

export function insertText(text) {
  restore();
  document.execCommand("insertText", false, text);
  emit();
}

export function applyInline(prop, val) {
  restore();
  const sel = window.getSelection();
  if (!sel.rangeCount) return;
  if (sel.isCollapsed) {
    const sp = document.createElement("span");
    sp.style[prop] = val;
    sp.textContent = "\u200B";
    sel.getRangeAt(0).insertNode(sp);
    const r = document.createRange();
    r.setStart(sp.firstChild, 1);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
    return emit();
  }
  document.execCommand("styleWithCSS", false, false);
  document.execCommand("fontSize", false, "7");
  const spans = [];
  active.querySelectorAll('font[size="7"]').forEach((f) => {
    const sp = document.createElement("span");
    sp.style[prop] = val;
    while (f.firstChild) sp.appendChild(f.firstChild);
    sp.querySelectorAll("span").forEach((x) => { x.style[prop] = ""; });
    f.replaceWith(sp);
    spans.push(sp);
  });
  if (spans.length) {
    const r = document.createRange();
    r.setStartBefore(spans[0]);
    r.setEndAfter(spans[spans.length - 1]);
    sel.removeAllRanges();
    sel.addRange(r);
  }
  emit();
}

export function selectedBlocks() {
  restore();
  const sel = window.getSelection();
  if (!sel.rangeCount) return [];
  const pick = () => {
    const r = sel.getRangeAt(0);
    return [...active.querySelectorAll(BLOCKS)].filter((b) => r.intersectsNode(b) && !b.querySelector(BLOCKS) && !b.classList.contains("page-break"));
  };
  let list = pick();
  if (!list.length) { document.execCommand("formatBlock", false, "p"); list = pick(); }
  return list;
}

export function setBlockStyle(prop, val) {
  selectedBlocks().forEach((b) => { b.style[prop] = val; });
  emit();
}

export function indent(dir) {
  const blocks = selectedBlocks();
  if (blocks.some((b) => b.closest("li"))) return exec(dir > 0 ? "indent" : "outdent");
  blocks.forEach((b) => {
    const v = Math.max(0, (parseFloat(b.style.marginLeft) || 0) + dir * 48);
    b.style.marginLeft = v ? `${v}px` : "";
  });
  emit();
}

export function setListStyle(type, ordered) {
  restore();
  const node = window.getSelection().anchorNode;
  const el = node?.nodeType === 3 ? node.parentElement : node;
  let list = el?.closest?.(ordered ? "ol" : "ul");
  if (!list) {
    exec(ordered ? "insertOrderedList" : "insertUnorderedList");
    const n2 = window.getSelection().anchorNode;
    list = (n2?.nodeType === 3 ? n2.parentElement : n2)?.closest?.(ordered ? "ol" : "ul");
  }
  if (list) list.style.listStyleType = type;
  emit();
}

export function currentCell() {
  if (!saved) return null;
  const n = saved.startContainer;
  const el = n.nodeType === 3 ? n.parentElement : n;
  return el?.closest?.("td,th") || null;
}

export function tableHTML(r, c) {
  const w = (100 / c).toFixed(2);
  const row = `<tr>${Array.from({ length: c }, () => `<td style="width:${w}%"><br></td>`).join("")}</tr>`;
  return `<table class="doc-table"><tbody>${row.repeat(r)}</tbody></table><p><br></p>`;
}

export function tableOp(op, opts = {}) {
  const td = currentCell();
  if (!td) return;
  const tr = td.parentElement;
  const table = td.closest("table");
  const idx = td.cellIndex;
  const blank = (c) => { const n = c.cloneNode(false); n.innerHTML = "<br>"; return n; };
  if (op === "rowAbove" || op === "rowBelow") {
    const nr = tr.cloneNode(false);
    [...tr.cells].forEach((c) => nr.appendChild(blank(c)));
    tr.parentElement.insertBefore(nr, op === "rowAbove" ? tr : tr.nextSibling);
  } else if (op === "colLeft" || op === "colRight") {
    [...table.rows].forEach((row) => {
      const cell = row.cells[Math.min(idx, row.cells.length - 1)];
      row.insertBefore(blank(cell), op === "colLeft" ? cell : cell.nextSibling);
    });
  } else if (op === "delRow") {
    tr.remove();
    if (!table.rows.length) table.remove();
  } else if (op === "delCol") {
    [...table.rows].forEach((row) => row.cells[idx]?.remove());
    if (!table.rows[0]?.cells.length) table.remove();
  } else if (op === "delTable") {
    table.remove();
  } else {
    const cells = {
      cell: [td],
      row: [...tr.cells],
      col: [...table.rows].map((r) => r.cells[idx]).filter(Boolean),
      table: [...table.querySelectorAll("td,th")],
    }[opts.scope || "cell"];
    cells.forEach((c) => {
      if (op === "shade") c.style.backgroundColor = opts.color;
      if (op === "border") c.style.border = opts.style === "none" ? "none" : `${opts.width}px ${opts.style} ${opts.color}`;
      if (op === "align") c.style.textAlign = opts.align;
      if (op === "valign") c.style.verticalAlign = opts.valign;
    });
  }
  emit();
}

export function wrapRange(cls, data) {
  restore();
  const sel = window.getSelection();
  if (!sel.rangeCount || sel.isCollapsed) return 0;
  const r = sel.getRangeAt(0);
  const base = r.commonAncestorContainer.nodeType === 3 ? r.commonAncestorContainer.parentNode : r.commonAncestorContainer;
  const parts = [];
  const w = document.createTreeWalker(base, NodeFilter.SHOW_TEXT);
  let n;
  while ((n = w.nextNode())) {
    if (!r.intersectsNode(n) || n.parentElement.closest('[contenteditable="false"]')) continue;
    const s = n === r.startContainer ? r.startOffset : 0;
    const e = n === r.endContainer ? r.endOffset : n.data.length;
    if (s < e && n.data.slice(s, e).trim()) parts.push([n, s, e]);
  }
  parts.forEach(([t, s, e]) => {
    let x = s > 0 ? t.splitText(s) : t;
    if (e - s < x.data.length) x.splitText(e - s);
    const sp = document.createElement("span");
    sp.className = cls;
    Object.assign(sp.dataset, data);
    x.replaceWith(sp);
    sp.appendChild(x);
  });
  sel.removeAllRanges();
  emit();
  return parts.length;
}
